import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useXR } from "@react-three/xr";
import * as THREE from "three";
import { RoomReflections } from "./RoomReflections";
import { boundedWorldLightSample, createLightCaptureWindow, stabilizeRoomLight, validLightPose } from "./roomLightingMath";

export default function XrRoomLighting({ enabled, lighting }) {
  const session = useXR(s => s.session);
  const player = useXR(s => s.player);
  const gl = useThree(s => s.gl);
  const active = useRef(null);
  const rig = useMemo(() => {
    const group = new THREE.Group(); group.name = "xr-room-lighting";
    const probe = new THREE.LightProbe(); probe.intensity = 0;
    const sun = new THREE.DirectionalLight(0xffffff, 0);
    group.add(probe, sun, sun.target);
    return { group, probe, sun, matrix: new THREE.Matrix4(),
      quaternion: new THREE.Quaternion(), direction: new THREE.Vector3(), color: new THREE.Color() };
  }, []);
  useEffect(() => {
    const state = { alive: true, probe: null, reflections: null, captured: false, window: createLightCaptureWindow() };
    active.current = state;
    lighting.current = { ...lighting.current, environment: null, status: enabled ? "fallback" : "off", calibration: null };
    rig.probe.intensity = 0; rig.sun.intensity = 0;
    Object.assign(rig.group.userData, { status: lighting.current.status, directGain: 0, diffuseGain: 0, probeGain: 0,
      sampleCount: 0, stabilizationSampleCount: 0, captured: false });
    const stop = () => {
      if (!state.alive) return;
      state.alive = false;
      lighting.current = { ...lighting.current, environment: null, status: "off", calibration: null };
      rig.probe.intensity = 0; rig.sun.intensity = 0;
      Object.assign(rig.group.userData, { status: "off", directGain: 0, diffuseGain: 0, probeGain: 0,
        sampleCount: 0, stabilizationSampleCount: 0, captured: false });
      state.reflections?.dispose(); state.reflections = null;
    };
    if (enabled && session?.requestLightProbe) {
      (async () => {
        try {
          const probe = await session.requestLightProbe({ reflectionFormat: session.preferredReflectionFormat || "srgba8" });
          if (!state.alive) return;
          state.probe = probe;
          if (window.XRWebGLBinding) {
            try { state.reflections = new RoomReflections(gl, session); }
            catch { /* Light estimates remain useful without reflection support. */ }
          }
        } catch { /* Optional feature denied/unsupported: retain showroom lighting. */ }
      })();
    }
    session?.addEventListener("end", stop);
    return () => { stop(); session?.removeEventListener("end", stop); };
  }, [enabled, session, gl, lighting, rig]);

  useFrame((_, delta, frame) => {
    const state = active.current;
    if (!enabled || !state?.alive || state.captured) return;
    let estimate, pose;
    if (state.probe && frame?.session === session && session.visibilityState === "visible") {
      try {
        estimate = frame.getLightEstimate?.(state.probe);
        pose = frame.getPose(state.probe.probeSpace, gl.xr.getReferenceSpace());
      } catch { /* Temporary tracking loss or unavailable feature. */ }
    }
    let sample = null;
    if (validLightPose(pose)) {
      player.updateWorldMatrix(true, false);
      rig.matrix.fromArray(pose.transform.matrix).premultiply(player.matrixWorld);
      if (validLightPose({ transform: { matrix: rig.matrix.elements } })) {
        rig.quaternion.setFromRotationMatrix(rig.matrix).normalize();
        sample = boundedWorldLightSample(estimate, rig.quaternion);
      }
    }
    const snapshot = stabilizeRoomLight(state.window, sample, delta);
    rig.group.userData.sampleCount = state.window.samples.length;
    rig.group.userData.stabilizationSampleCount = state.window.samples.length;
    if (snapshot) {
      // Capture a short robust window once, then hold it through head movement,
      // lost tracking, focus changes and reflectionchange events.
      rig.probe.sh.fromArray(snapshot.sh);
      const intensity = Math.max(...snapshot.directRGB);
      rig.color.setRGB(...snapshot.directRGB.map(value => value / (intensity || 1)));
      rig.sun.color.copy(rig.color);
      rig.sun.intensity = intensity;
      rig.direction.fromArray(snapshot.direction);
      rig.sun.position.copy(rig.direction).multiplyScalar(10);
      if (state.reflections) {
        try {
          lighting.current.environment = state.reflections.update(state.probe, rig.quaternion);
        } catch {
          lighting.current.environment = null;
          state.reflections.dispose(); state.reflections = null;
        }
      }
      // A room cube already supplies diffuse irradiance; avoid adding it twice.
      // The SH window already contains the uniform diffuse gain. Applying the
      // gain again here would darken it twice.
      rig.probe.intensity = lighting.current.environment ? 0 : 1;
      lighting.current.status = lighting.current.environment ? "room-reflections" : "room-light";
      state.captured = true;
      const probeGain = lighting.current.environment ? 0 : snapshot.diffuseGain;
      lighting.current.calibration = { directGain: snapshot.directGain, diffuseGain: snapshot.diffuseGain, probeGain,
        sampleCount: snapshot.sampleCount, stabilizedSeconds: snapshot.validSeconds, pose: [...rig.matrix.elements] };
      Object.assign(rig.group.userData, { directGain: snapshot.directGain, diffuseGain: snapshot.diffuseGain, probeGain,
        captured: true });
    }
    rig.group.userData.status = lighting.current.status;
  }, -2);
  return <primitive object={rig.group} />;
}
